import { useTranslation } from 'react-i18next';
import { Badge, Box, Flex, Spinner, Table, Text } from '@chakra-ui/react';
import {
  useAudienceDetail,
  useDeliveryReport,
} from '../../../hooks/queries/useAlerts';
import type {
  AudienceFunnelStep,
  DeliveryReportFunnel,
} from '../../../services/alerts.service';

const TYPE_ORDER = ['start', 'end', 'other'] as const;

interface FunnelBarProps {
  label: string;
  value: number;
  maxValue: number;
  colorPalette: string;
}

function FunnelBar({ label, value, maxValue, colorPalette }: FunnelBarProps) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <Flex align="center" gap={3} py={1}>
      <Text minW={32} fontSize="sm" color="text.secondary">
        {label}
      </Text>
      <Box flex={1} bg="bg.muted" borderRadius="sm" h={5} overflow="hidden">
        <Box
          h="100%"
          w={`${pct}%`}
          bg={`${colorPalette}.solid`}
          borderRadius="sm"
          transition="width 0.3s"
        />
      </Box>
      <Text minW={12} fontSize="sm" fontWeight="medium" textAlign="right">
        {value}
      </Text>
    </Flex>
  );
}

interface AudienceFunnelStepsProps {
  steps: AudienceFunnelStep[];
  maxValue: number;
}

function AudienceFunnelSteps({ steps, maxValue }: AudienceFunnelStepsProps) {
  const { t } = useTranslation();
  const exclusions = steps.filter((s) => s.before > s.after);
  if (exclusions.length === 0) return null;

  const funnelStepLabels: Record<string, string> = {
    base_users: t('events.inactiveUsers', 'Inactive Users'),
    internal_filter: t('events.internalAlertOptOut', 'Internal Alert Opt-out'),
    pending_approval_filter: t('events.approvalPermission', 'Approval Permission'),
    opt_in_preference: t('events.pushNotificationOptOut', 'Push Notification Opt-out'),
    check_in_filter: t('events.notCheckedIn', 'Not Checked In'),
  };

  return (
    <Box pl={6} ml={4} borderLeftWidth={2} borderLeftColor="orange.muted">
      {exclusions.map((step) => (
        <FunnelBar
          key={step.name}
          label={funnelStepLabels[step.name] ?? step.name}
          value={step.before - step.after}
          maxValue={maxValue}
          colorPalette="orange"
        />
      ))}
    </Box>
  );
}

interface AlertHistoryDeliveryTabProps {
  alertId: number;
  enabled?: boolean;
}

export function AlertHistoryDeliveryTab({
  alertId,
  enabled = true,
}: AlertHistoryDeliveryTabProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useDeliveryReport(alertId, enabled);

  const { data: audience, isLoading: audienceLoading } = useAudienceDetail(
    alertId,
    enabled,
    !!data?.audience_detail_available,
  );

  const typeLabels: Record<'start' | 'end' | 'other', string> = {
    start: t('events.alertStart', 'Alert Start'),
    end: t('events.alertEnd', 'Alert End'),
    other: t('events.otherPolice', 'Other (Police, etc.)'),
  };

  const renderFunnelBars = (funnel: DeliveryReportFunnel, max: number) => {
    return (
      <>
        <FunnelBar
          label={t('events.pushSent', 'Push Sent')}
          value={funnel.push_sent}
          maxValue={max}
          colorPalette="green"
        />
        <FunnelBar
          label={t('events.pushFailed', 'Push Failed')}
          value={funnel.push_failed}
          maxValue={max}
          colorPalette="red"
        />
        {funnel.sms_sent > 0 && (
          <FunnelBar
            label={t('events.smsSent', 'SMS Sent')}
            value={funnel.sms_sent}
            maxValue={max}
            colorPalette="teal"
          />
        )}
        {funnel.sms_failed > 0 && (
          <FunnelBar
            label={t('events.smsFailed', 'SMS Failed')}
            value={funnel.sms_failed}
            maxValue={max}
            colorPalette="red"
          />
        )}
        {funnel.email_sent > 0 && (
          <FunnelBar
            label={t('events.emailSent', 'Email Sent')}
            value={funnel.email_sent}
            maxValue={max}
            colorPalette="purple"
          />
        )}
        {funnel.email_failed > 0 && (
          <FunnelBar
            label={t('events.emailFailed', 'Email Failed')}
            value={funnel.email_failed}
            maxValue={max}
            colorPalette="red"
          />
        )}
      </>
    );
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error || !data) {
    return (
      <Box py={6} textAlign="center">
        <Text color="text.secondary">
          {t('events.noDeliveryData', 'No delivery data available for this alert.')}
        </Text>
      </Box>
    );
  }

  const {
    funnel,
    by_type,
    delivery_summary,
    failure_reasons,
    targeted_user_count,
    no_token_count,
  } = data;
  const maxFunnel = targeted_user_count ?? funnel.push_attempted;

  return (
    <Box py={4} px={2}>
      <Text fontWeight="semibold" fontSize="md" mb={3}>
        {t('events.notificationFunnel', 'Notification Funnel')}
      </Text>
      <Box mb={6}>
        {targeted_user_count != null ? (
          <FunnelBar
            label={t('events.targetedUsers', 'Targeted Users')}
            value={targeted_user_count}
            maxValue={maxFunnel}
            colorPalette="blue"
          />
        ) : (
          <Flex align="center" gap={3} py={1}>
            <Text minW={32} fontSize="sm" color="text.secondary">
              {t('events.targetedUsers', 'Targeted Users')}
            </Text>
            <Text fontSize="sm" color="text.muted">
              {t('events.naPreTracking', 'N/A (pre-tracking)')}
            </Text>
          </Flex>
        )}
        {data.audience_detail_available && audienceLoading && (
          <Flex align="center" gap={2} py={1} pl={6} ml={4}>
            <Spinner size="xs" />
            <Text fontSize="xs" color="text.muted">
              {t('events.loadingAudienceBreakdown', 'Loading audience breakdown...')}
            </Text>
          </Flex>
        )}
        {audience && (
          <AudienceFunnelSteps steps={audience.funnel} maxValue={maxFunnel} />
        )}
        {no_token_count != null && (
          <FunnelBar
            label={t('events.noAppInstalled', 'No App Installed')}
            value={no_token_count}
            maxValue={maxFunnel}
            colorPalette="gray"
          />
        )}
        {renderFunnelBars(funnel, maxFunnel)}
      </Box>

      {by_type && Object.keys(by_type).length > 0 && (
        <Box mb={6}>
          <Text fontWeight="semibold" fontSize="md" mb={3}>
            {t('events.byNotificationType', 'By Notification Type')}
          </Text>
          {TYPE_ORDER.map((key) => {
            const typeFunnel = by_type[key];
            if (!typeFunnel) return null;
            return (
              <Box key={key} mb={4}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="text.secondary"
                  mb={1}
                >
                  {typeLabels[key]}
                </Text>
                {renderFunnelBars(typeFunnel, typeFunnel.push_attempted)}
              </Box>
            );
          })}
        </Box>
      )}

      <Text fontWeight="semibold" fontSize="md" mb={3}>
        {t('events.confirmedDelivery', 'Confirmed Delivery')}
      </Text>
      <Flex gap={4} mb={6} wrap="wrap">
        <Flex
          direction="column"
          align="center"
          p={3}
          bg="bg.muted"
          borderRadius="md"
          minW={20}
        >
          <Text fontSize="2xl" fontWeight="bold">
            {delivery_summary.confirmed_fcm}
          </Text>
          <Text fontSize="xs" color="text.secondary">
            {t('events.push', 'Push')}
          </Text>
        </Flex>
        {delivery_summary.confirmed_sms > 0 && (
          <Flex
            direction="column"
            align="center"
            p={3}
            bg="bg.muted"
            borderRadius="md"
            minW={20}
          >
            <Text fontSize="2xl" fontWeight="bold">
              {delivery_summary.confirmed_sms}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {t('events.sms', 'SMS')}
            </Text>
          </Flex>
        )}
        {delivery_summary.confirmed_email > 0 && (
          <Flex
            direction="column"
            align="center"
            p={3}
            bg="bg.muted"
            borderRadius="md"
            minW={20}
          >
            <Text fontSize="2xl" fontWeight="bold">
              {delivery_summary.confirmed_email}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {t('events.email', 'Email')}
            </Text>
          </Flex>
        )}
        {delivery_summary.pending > 0 && (
          <Flex
            direction="column"
            align="center"
            p={3}
            bg="bg.muted"
            borderRadius="md"
            minW={20}
          >
            <Text fontSize="2xl" fontWeight="bold" color="fg.warning">
              {delivery_summary.pending}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {t('sso.pending', 'Pending')}
            </Text>
          </Flex>
        )}
      </Flex>

      {failure_reasons.length > 0 && (
        <>
          <Text fontWeight="semibold" fontSize="md" mb={3}>
            {t('events.failureReasons', 'Failure Reasons')}
          </Text>
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>{t('events.channel', 'Channel')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('events.reason', 'Reason')}</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">{t('events.count', 'Count')}</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {failure_reasons.map((reason, idx) => (
                <Table.Row key={idx}>
                  <Table.Cell>
                    <Badge
                      size="sm"
                      colorPalette={
                        reason.channel === 'fcm'
                          ? 'blue'
                          : reason.channel === 'sms'
                            ? 'teal'
                            : 'purple'
                      }
                    >
                      {reason.channel.toUpperCase()}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" truncate maxW={64}>
                      {reason.failure_reason}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right">{reason.count}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </>
      )}
    </Box>
  );
}
